import React, { useState, useId } from "react";
import { MdExpandMore } from "react-icons/md";
import { MdWarning } from "react-icons/md";
import clsx from "clsx";
import { MenuItem } from "../../redux/slices/menuSlice";

interface AllergensSectionProps {
  product: MenuItem;
}

const AllergensSection: React.FC<AllergensSectionProps> = ({ product }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = useId();

  if (!product.allergens || product.allergens.length === 0) return null;

  return (
    <section
      aria-labelledby={`${contentId}-title`}
      className="mb-6 sm:mb-8 rounded-lg border border-gray-200 bg-white overflow-hidden"
    >
      {/* Toggle Button */}
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={() => setIsExpanded((prev) => !prev)}
        className={clsx(
          "w-full flex items-center justify-between text-left",
          "px-3 sm:px-4 py-2.5 sm:py-3",
          "bg-gray-50 hover:bg-gray-100 transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-600"
        )}
      >
        <h2
          id={`${contentId}-title`}
          className="flex items-center gap-2 text-sm sm:text-base md:text-lg font-semibold"
        >
          <MdWarning className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 shrink-0" />
          Allergens
        </h2>

        <MdExpandMore
          aria-hidden
          className={clsx(
            "h-5 w-5 text-gray-500 transition-transform duration-300 ease-in-out",
            isExpanded && "rotate-180"
          )}
        />
      </button>

      {/* Animated Allergens List */}
      <div
        id={contentId}
        role="region"
        aria-hidden={!isExpanded}
        className={clsx(
          "flex flex-wrap gap-2 px-3 sm:px-4 transition-[max-height,opacity,padding] duration-300 ease-in-out",
          isExpanded
            ? "max-h-96 py-3 sm:py-4 opacity-100"
            : "max-h-0 py-0 opacity-0 overflow-hidden pointer-events-none"
        )}
      >
        {product.allergens.map((allergen) => (
          <AllergenBadge key={allergen} allergen={allergen} />
        ))}
      </div>
    </section>
  );
};

interface AllergenBadgeProps {
  allergen: string;
}

const AllergenBadge: React.FC<AllergenBadgeProps> = React.memo(({ allergen }) => {
  return (
    <span
      title={allergen} // full text on hover
      className={clsx(
        "px-2 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs md:text-sm",
        "bg-yellow-50 text-yellow-800 border border-yellow-200",
        "truncate max-w-full whitespace-nowrap"
      )}
    >
      {allergen}
    </span>
  );
});

AllergenBadge.displayName = "AllergenBadge";

export default AllergensSection;
